import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wrench, CheckCircle, Award, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Installation = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    installationType: '',
    poleHeight: '',
    propertyType: '',
    additionalInfo: '',
    preferredDate: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(false);
    
    // Send to backend API
    fetch(`${BACKEND_URL}/api/orders/installation-quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      setSubmitted(true);
      toast.success('Installation quote request submitted! Check your email at coastalflagco@yahoo.com');
    })
    .catch(error => {
      console.error('Error:', error);
      toast.error('Failed to submit request. Please call 205-587-2402');
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Quote Request Received!</h2>
            <p className="text-slate-600 mb-6">
              Thank you for your interest in our professional flagpole installation service. One of our experienced team members will contact you within 24 hours to discuss your project and provide a detailed quote.
            </p>
            <Button onClick={() => window.location.href = '/'} className="bg-red-600 hover:bg-red-700">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-white py-12 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Wrench className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-5xl font-bold text-slate-900 mb-4">Professional Flagpole Installation</h1>
            <p className="text-xl text-slate-600">
              Expert installation services for residential and commercial properties throughout Alabama. Licensed, insured, and experienced.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Form */}
          <Card>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Get Your Free Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="205-555-0123"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Installation Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    placeholder="123 Main Street"
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      placeholder="Birmingham"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      required
                      placeholder="35209"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="propertyType">Property Type *</Label>
                  <Select onValueChange={(value) => handleSelectChange('propertyType', value)} required>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="government">Government</SelectItem>
                      <SelectItem value="school">School/Educational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="installationType">Installation Type *</Label>
                    <Select onValueChange={(value) => handleSelectChange('installationType', value)} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New Installation</SelectItem>
                        <SelectItem value="replacement">Replacement</SelectItem>
                        <SelectItem value="repair">Repair/Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="poleHeight">Pole Height *</Label>
                    <Select onValueChange={(value) => handleSelectChange('poleHeight', value)} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select height" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15-20">15-20 feet</SelectItem>
                        <SelectItem value="20-25">20-25 feet</SelectItem>
                        <SelectItem value="25-30">25-30 feet</SelectItem>
                        <SelectItem value="30-40">30-40 feet</SelectItem>
                        <SelectItem value="40+">40+ feet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="preferredDate">Preferred Installation Date</Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="additionalInfo">Additional Information</Label>
                  <Textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    placeholder="Any special requirements, site conditions, or questions?"
                    className="mt-2 min-h-[100px]"
                  />
                </div>

                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-lg py-6">
                  Request Free Quote
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="space-y-6">
            <Card className="bg-slate-900 text-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6">Why Choose Our Installation Service?</h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Award className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Nearly 100 Years Experience</h4>
                      <p className="text-slate-300 text-sm">Family owned business with generations of expertise</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Shield className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Licensed & Insured</h4>
                      <p className="text-slate-300 text-sm">Fully licensed contractors with comprehensive insurance</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Clock className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Professional & Efficient</h4>
                      <p className="text-slate-300 text-sm">Timely installations with minimal disruption</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">Complete Service</h4>
                      <p className="text-slate-300 text-sm">From site prep to final setup and cleanup</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">What's Included</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Site evaluation and preparation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Professional-grade materials
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Concrete foundation installation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Flagpole assembly and mounting
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Hardware and accessories setup
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Flag installation and demonstration
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Site cleanup
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Warranty and maintenance guidance
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-blue-600 text-white">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-4">Service Areas</h3>
                <p className="mb-4">We provide professional flagpole installation throughout:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Sandestin</li>
                  <li>• Santa Rosa Beach</li>
                  <li>• Panama City</li>
                  <li>• Destin</li>
                  <li>• 30A</li>
                  <li>• And all Gulf Coast locations</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-red-600 text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-2">Have Questions?</h3>
                <p className="mb-4">Call us for immediate assistance</p>
                <a href="tel:205-587-2402" className="text-3xl font-bold hover:underline block mb-2">
                  205-587-2402
                </a>
                <p className="text-sm">Monday - Friday: 8am - 5pm</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Installation;